/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { GuidedExpressionEditor } from "abreact/expression/ui/editor/GuidedExpressionEditor";
import { OperandValueSummaryGeneratorImpl } from "abreact/expression/ui/editor/impl/factory/OperandValueSummaryGeneratorImpl";
import { AbstractModelImplBaseRecordClassFactory } from "abreact/expression/ui/editor/impl/model/impl/AbstractModelImplBaseRecordClassFactory";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { ReferenceOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/ReferenceOperandValueImpl";
import { RowBasedRowModelImpl } from "abreact/expression/ui/editor/impl/model/impl/row/RowBasedRowModelImpl";
import { GuidedExpressionEditorUtils } from "abreact/expression/ui/editor/impl/utils/GuidedExpressionEditorUtils";
import { SimpleSummaryExpressionDefinitionExportVisitorImpl } from "abreact/expression/utils/SimpleSummaryExpressionDefinitionExportVisitorImpl";
import { AbBooleanLogicGroupingContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicGroupingContent";
import { AbBooleanLogicSingleConditionContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicSingleConditionContent";
import { AbBooleanLogicTreeContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeContent";
import { BooleanLogicTreeContent } from "abreact/incubator/ui/abBooleanLogicTree/model/BooleanLogicTreeContent";
import { Assert } from "abweb/utils/Assert";
import Immutable from "immutable";
const defaultState = {
  id: undefined,
  // Defined in constructor
  generateId: undefined,
  // Defined in constructor
  operatorRoleID: undefined,
  // Defined in constructor
  booleanLogicTreeContent: new BooleanLogicTreeContent(),
  joinOperandValuesByGroupingContentKey: Immutable.Map()
};
export class AbstractRowBasedModelImpl extends new AbstractModelImplBaseRecordClassFactory().createBaseRecordClass(defaultState) {
  constructor(specification, operatorRoleID) {
    const {
      expressionDomainModel
    } = specification;
    expressionDomainModel.getExpOperatorRole(operatorRoleID);
    let booleanLogicTreeContent = new BooleanLogicTreeContent();
    const joinOperandValuesByGroupingContentKey = new Map();
    const rowModelsByRowKey = new Map();
    let rootJoinOperandValue;
    let rootGroupingContent;
    const {
      expExpressionDefinition
    } = specification;
    if (expExpressionDefinition != null) {
      Assert.assert(!Expression.Definition.isNonBooleanDefinition(expExpressionDefinition));
      Assert.assert(Expression.Domain.OperatorRole.getID(expExpressionDefinition.rootNode.nodeType) === operatorRoleID);
      super({
        id: expExpressionDefinition.id ?? specification.generateId(),
        generateId: specification.generateId,
        operatorRoleID: operatorRoleID
      });
      rootJoinOperandValue = this.buildRootJoinOperandValue(expExpressionDefinition.rootNode, {
        expressionDomainModel: expressionDomainModel,
        summaryGenerator: specification.summaryGenerator,
        expTemplateConfiguration: expExpressionDefinition.templateConfiguration
      });
      if (OperatorOperandValueImpl.isInstance(rootJoinOperandValue)) {
        Assert.assert(Expression.Domain.OperatorRole.isJoin(rootJoinOperandValue.expOperator));
      }
      rootGroupingContent = this.buildRootGroupingContent(rootJoinOperandValue, expressionDomainModel, rowModelsByRowKey, joinOperandValuesByGroupingContentKey);
      rootGroupingContent = rootGroupingContent.withDisabled(rootJoinOperandValue.isDisabledOrDefault);
    } else {
      const generateId = GuidedExpressionEditorUtils.createGenerateIdFunction();
      super({
        id: generateId(),
        generateId: generateId,
        operatorRoleID: operatorRoleID
      });
      rootJoinOperandValue = this.createRootJoinOperandValue(expressionDomainModel);
      rootGroupingContent = new AbBooleanLogicGroupingContent(rootJoinOperandValue.id, this.defaultConnective);
      if (specification.withSingleRow) {
        const id = generateId();
        rootGroupingContent = rootGroupingContent.withAppendSingleRow(new AbBooleanLogicSingleConditionContent(id));
        const rowModel = this.createDefaultRowModel(id, expressionDomainModel, specification.expTemplateConfiguration);
        rowModelsByRowKey.set(rowModel.id, rowModel);
      }
    }
    booleanLogicTreeContent = booleanLogicTreeContent.withTreeContent(new AbBooleanLogicTreeContent(rootGroupingContent)).withItemTable(Immutable.Map(rowModelsByRowKey));
    joinOperandValuesByGroupingContentKey.set(rootGroupingContent.key, rootJoinOperandValue);
    for (let [groupingContentKey, joinOperandValue] of joinOperandValuesByGroupingContentKey.entries()) {
      if (OperatorOperandValueImpl.isInstance(joinOperandValue)) {
        Assert.assert(joinOperandValue.sourceOperandValue == null);
        joinOperandValue = joinOperandValue.withArgumentOperandValuesByOperandName(Immutable.Map());
      } else {
        joinOperandValue = joinOperandValue.withOperandValuesByNodeId(Immutable.Map());
      }
      joinOperandValuesByGroupingContentKey.set(groupingContentKey, joinOperandValue);
    }
    return this.with({
      booleanLogicTreeContent: booleanLogicTreeContent,
      joinOperandValuesByGroupingContentKey: Immutable.Map(joinOperandValuesByGroupingContentKey)
    });
  }
  createDefaultRowModel(id, expressionDomainModel, expTemplateConfiguration) {
    let rowModel = RowBasedRowModelImpl.create(id);
    const placeholderExpOperator = expressionDomainModel.findPlaceholderExpOperatorForExpOperatorRole(this.operatorRoleID);
    if (placeholderExpOperator != null) {
      const operatorOperandValue = new OperatorOperandValueImpl({
        id: rowModel.id,
        ...GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue.partialStateFromExpOperator(placeholderExpOperator, expressionDomainModel)
      });
      rowModel = rowModel.withOperatorOperandValue(operatorOperandValue);
    }
    if (expTemplateConfiguration != null) {
      const operandValue = ReferenceOperandValueImpl.fromExpTemplateConfiguration(this.generateId(), expressionDomainModel.defaultSourceOperandName, expTemplateConfiguration, expressionDomainModel);
      rowModel = rowModel.withSourceOperandValue(operandValue);
    }
    return rowModel;
  }
  get defaultConnective() {
    return Expression.Domain.Constants.AND_OPERATOR_ID;
  }
  createConditionContent(operatorOperandValue, rowModelsByRowKey) {
    const rowModel = RowBasedRowModelImpl.fromOperatorOperandValue(operatorOperandValue);
    rowModelsByRowKey.set(rowModel.id, rowModel);
    const conditionContent = new AbBooleanLogicSingleConditionContent(rowModel.id);
    return conditionContent.withDisabled(operatorOperandValue.isDisabledOrDefault);
  }
  get rowKeys() {
    return this.booleanLogicTreeContent.itemContentTable.keySeq().toArray();
  }
  get rowModels() {
    return this.booleanLogicTreeContent.itemContentTable.valueSeq().toArray();
  }
  isEmpty() {
    this.booleanLogicTreeContent.assertKeysConsistent();
    return this.booleanLogicTreeContent.itemContentTable.isEmpty();
  }
  isComplete() {
    this.booleanLogicTreeContent.assertKeysConsistent();
    return this.booleanLogicTreeContent.itemContentTable.valueSeq().toArray().every(rowModel => rowModel.isComplete());
  }
  withJoinOperandValuesByGroupingContentKey(joinOperandValuesByGroupingContentKey) {
    return this.with({
      joinOperandValuesByGroupingContentKey
    });
  }
  withBooleanLogicTreeContent(booleanLogicTreeContent) {
    booleanLogicTreeContent.assertKeysConsistent();
    return this.with({
      booleanLogicTreeContent
    });
  }
  findRowModel(rowKey) {
    return this.booleanLogicTreeContent.getItemContent(rowKey);
  }
  getRowModel(rowKey) {
    const rowModel = this.findRowModel(rowKey);
    Assert.assert(rowModel != null);
    return rowModel;
  }
  findJoinOperandValue(groupingContentKey) {
    return this.joinOperandValuesByGroupingContentKey.get(groupingContentKey);
  }
  getJoinOperandValue(groupingContentKey) {
    const joinOperandValue = this.findJoinOperandValue(groupingContentKey);
    Assert.assert(joinOperandValue != null);
    return joinOperandValue;
  }
  getRowPositionInGroup(rowKey) {
    const {
      nestingState
    } = this.booleanLogicTreeContent.treeContent;
    let rowPositionInGroup = AbstractRowBasedModelImpl.walkSequenceAndFindRowPositionInGroup(rowKey, nestingState.sequence);
    return rowPositionInGroup || "OTHER";
  }
  generateFullyQualifiedDataTestId(rowKey) {
    const {
      nestingState
    } = this.booleanLogicTreeContent.treeContent;
    let dataTestIdElements = [];
    AbstractRowBasedModelImpl.walkSequence(nestingState.sequence, (rowOrGroup, indexByCardinality) => AbstractRowBasedModelImpl.walkSequenceAndGatherDataTestIdElements(rowKey, rowOrGroup, dataTestIdElements, indexByCardinality));
    dataTestIdElements.unshift(`${nestingState.connective.toLowerCase()}1`);
    return dataTestIdElements.join(".");
  }
  static walkSequenceAndFindRowPositionInGroup(rowKey, sequence) {
    let rowPositionInGroup;
    for (let i = 0; i < sequence.size; i++) {
      let element = sequence.get(i);
      if (element == null) {
        continue;
      }
      if (element.key === rowKey) {
        if (i === 0) {
          rowPositionInGroup = "FIRST";
          break;
        } else if (i === sequence.size - 1) {
          rowPositionInGroup = "LAST";
          break;
        } else {
          rowPositionInGroup = "OTHER";
          break;
        }
      }
      if (element.cardinality === "GROUP") {
        rowPositionInGroup = AbstractRowBasedModelImpl.walkSequenceAndFindRowPositionInGroup(rowKey, element.sequence);
        if (rowPositionInGroup) {
          break;
        }
      }
    }
    return rowPositionInGroup;
  }
  static walkSequence(sequence, predicate) {
    let conditionIndex = 0;
    const connectiveIndexMap = {
      "AND": 0,
      "OR": 0
    };
    let matched = false;
    for (let index = 0; index < sequence.size; index++) {
      const element = sequence.get(index);
      if (element == null) {
        continue;
      }
      const specificIndex = element.cardinality === "GROUP" ? connectiveIndexMap[element.connective]++ : conditionIndex++;
      if (predicate(element, specificIndex)) {
        matched = true;
        break;
      }
    }
    return matched;
  }
  static walkSequenceAndGatherDataTestIdElements(key, rowOrGroup, dataTestIdElements, currIndex) {
    let matched = false;
    if (rowOrGroup.cardinality === "SINGLE") {
      if (rowOrGroup.key === key) {
        dataTestIdElements.push(`c${currIndex + 1}`);
        matched = true;
      }
    } else {
      if (rowOrGroup.key === key) {
        dataTestIdElements.unshift(`${rowOrGroup.connective.toLowerCase()}${currIndex + 1}`);
        matched = true;
      } else {
        matched = this.walkSequence(rowOrGroup.sequence, (currElement, elementIndexByType) => {
          if (this.walkSequenceAndGatherDataTestIdElements(key, currElement, dataTestIdElements, elementIndexByType)) {
            dataTestIdElements.unshift(`${rowOrGroup.connective.toLowerCase()}${currIndex + 1}`);
            return true;
          } else {
            return false;
          }
        });
      }
    }
    return matched;
  }
  async export(expressionDomainModel, options) {
    let {
      exportVisitor,
      expTemplateConfiguration
    } = options ?? {};
    exportVisitor ??= this.getDefaultExpressionDefinitionCreationVisitor(expressionDomainModel);
    const {
      nestingState: rootGroupingContent
    } = this.booleanLogicTreeContent.treeContent;
    const rootExpExpressionNode = await this.buildRootExpExpressionNode(rootGroupingContent, expressionDomainModel, exportVisitor);
    await exportVisitor.endJoinExpExpressionNode?.(rootExpExpressionNode);
    return this.createExpExpressionDefinition(expressionDomainModel, rootExpExpressionNode, exportVisitor, expTemplateConfiguration);
  }
  getDefaultExpressionDefinitionCreationVisitor(expressionDomainModel) {
    return new SimpleSummaryExpressionDefinitionExportVisitorImpl(new OperandValueSummaryGeneratorImpl(), expressionDomainModel);
  }
  async buildRootExpExpressionNode(rootGroupingContent, expressionDomainModel, exportVisitor) {
    const rootJoinOperandValue = this.getJoinOperandValue(rootGroupingContent.key);
    const rootExpExpressionNode = rootJoinOperandValue.toExpExpressionNode();
    await exportVisitor.startJoinExpExpressionNode?.(rootExpExpressionNode);
    await this.exportGroupRecursive(rootGroupingContent, rootExpExpressionNode, expressionDomainModel, exportVisitor);
    return rootExpExpressionNode;
  }
  async exportGroupRecursive(groupingContent, parentExpExpressionNode, expressionDomainModel, exportVisitor) {
    for (const child of groupingContent.sequence.toArray()) {
      let expExpressionNode;
      if (child.cardinality === "GROUP") {
        const joinOperandValue = this.getJoinOperandValue(child.key);
        expExpressionNode = joinOperandValue.toExpExpressionNode();
        await exportVisitor.startJoinExpExpressionNode?.(expExpressionNode);
        await this.exportGroupRecursive(child, expExpressionNode, expressionDomainModel, exportVisitor);
        await exportVisitor.endJoinExpExpressionNode?.(expExpressionNode);
        expExpressionNode.disabled = child.disabled || undefined;
      } else {
        expExpressionNode = await this.exportRow(child, exportVisitor);
      }
      if (expExpressionNode != null) {
        parentExpExpressionNode.operandValues ??= [];
        parentExpExpressionNode.operandValues.push(expExpressionNode);
      }
    }
  }
  async exportRow(row, exportVisitor) {
    const rowModel = this.booleanLogicTreeContent.getItemContent(row.key);
    Assert.assert(rowModel != null);
    const expExpressionNode = rowModel.export();
    expExpressionNode.disabled = row.disabled;
    await exportVisitor.visitRowExpExpressionNode?.(expExpressionNode, rowModel);
    return expExpressionNode;
  }
}
//# sourceMappingURL=AbstractRowBasedModelImpl.js.map
